<template>
  <Layout>
    <div class="bg-main flex h-screen overflow-hidden">
      <div class="flex-none transition-all duration-300">
        <SidebarManagement
          className="!items-center !pb-6 !pt-10"
          :isShowLogo="false"
          :isShowUsername="true"
          :userName="layoutData.userName"
          :heightItem="750"
          :menuList="layoutData.dataList"
          :onClickSubMenu="onClickMenu"
        />
      </div>
      <div class="flex-1 overflow-auto transition-all duration-300">
        <slot />
      </div>
    </div>

    <Notifications position="top center" :group="'tailwind'">
      <template #body="props">
        <Toast
          class="mt-2 mr-6"
          :isError="props.item.data.isError"
          :isWarning="props.item.data.isWarning"
          :isNotice="props.item.data.isNotice"
          :isDisabled="props.item.data.isDisabled"
          :label="props.item.text"
          :onClick="props.close"
        />
      </template>
    </Notifications>

    <!-- Loading Overlay -->
    <LoadingOverlay
      v-if="isShowLoading"
      :isFlow="true"
      :isPulse="false"
      :isCircleFade="false"
    />
  </Layout>
</template>

<script setup>
import 'prismjs/components/'; // Add other languages as needed
import 'prismjs/prism'; // Core
import 'prismjs/themes/prism.css'; // Use another theme if you prefer
const route = useRoute();
const { $router, $constants, $functions, $t } = useNuxtApp();
import { useI18n } from 'vue-i18n';
const { t, locale } = useI18n();

const isShowLoading = useIsShowLoading();
const gUserAuthData = useUserAuthData();

const updateMenuTranslations = () => {
  layoutData.dataList = layoutData.dataList.map((menu) => {
    if (menu.id === $constants.ENUM.SOCIAL_MENU.AI_MANAGEMENT) {
      menu.name = t('sidebar.aiManagement');
    } else if (menu.id === $constants.ENUM.SOCIAL_MENU.KNOWLEDGE_BASE) {
      menu.name = t('sidebar.knowledgeBase');
    } else if (menu.id === $constants.ENUM.SOCIAL_MENU.SEMANTIC_CACHE) {
      menu.name = t('sidebar.semanticCache');
    } else if (menu.id === $constants.ENUM.SOCIAL_MENU.CONVERSATION_HISTORY) {
      menu.name = t('sidebar.conversationHistory');
    } else if (menu.id === $constants.ENUM.SOCIAL_MENU.ACCESS_MANAGEMENT) {
      menu.name = t('sidebar.accessManagement');
    } else if (menu.id === $constants.ENUM.SOCIAL_MENU.AI_ACCURACY_REPORT) {
      menu.name = t('sidebar.aiAccuracyReport');
    } else if (menu.id === $constants.ENUM.SOCIAL_MENU.HISTORY_LOG) {
      menu.name = t('sidebar.historyLog');
    }

    // Update subMenu translations
    if (menu.subMenu && menu.subMenu.length > 0) {
      menu.subMenu = menu.subMenu.map((sub) => {
        if (sub.id === $constants.ENUM.SOCIAL_MENU.MANAGE_ROLE) {
          sub.name = t('sidebar.manageRole');
        } else if (sub.id === $constants.ENUM.SOCIAL_MENU.MANAGE_USER) {
          sub.name = t('sidebar.manageUser');
        }
        return sub;
      });
    }

    return menu;
  });
};

watch(() => locale.value, () => {
  updateMenuTranslations();
});

const layoutData = reactive({
  userName: 'Admin',
  dataList: [
    {
      id: $constants.ENUM.SOCIAL_MENU.AI_MANAGEMENT,
      icon: '/images/icons/ic-ai-management.svg',
      iconActive: '/images/icons/ic-ai-management.svg',
      name: t('sidebar.aiManagement'),
      path: $constants.ROUTE.AI_MANAGEMENT.LINK,
      activeNameList: [
        $constants.ROUTE.AI_MANAGEMENT.NAME,
        $constants.ROUTE.AI_MANAGEMENT_MANAGE.NAME,
      ],
    },
    {
      id: $constants.ENUM.SOCIAL_MENU.KNOWLEDGE_BASE,
      icon: '/images/icons/ic-knowledge-base.svg',
      iconActive: '/images/icons/ic-knowledge-base.svg',
      name: t('sidebar.knowledgeBase'),
      path: $constants.ROUTE.KNOWLEDGE_BASE.LINK,
      activeNameList: [
        $constants.ROUTE.KNOWLEDGE_BASE.NAME,
        $constants.ROUTE.KNOWLEDGE_BASE_MANAGE.NAME,
      ],
    },
    {
      id: $constants.ENUM.SOCIAL_MENU.SEMANTIC_CACHE,
      icon: '/images/icons/ic-knowledge-base.svg',
      iconActive: '/images/icons/ic-knowledge-base.svg',
      name: t('sidebar.semanticCache'),
      path: $constants.ROUTE.SEMANTIC_CACHE.LINK,
      activeNameList: [
        $constants.ROUTE.SEMANTIC_CACHE.NAME,
        $constants.ROUTE.SEMANTIC_CACHE_MANAGE.NAME,
        $constants.ROUTE.SEMANTIC_CACHE_PLAYGROUND.NAME,
      ],
    },
    {
      id: $constants.ENUM.SOCIAL_MENU.CONVERSATION_HISTORY,
      icon: '/images/icons/ic-conversation-history.svg',
      iconActive: '/images/icons/ic-conversation-history.svg',
      name: t('sidebar.conversationHistory'),
      path: $constants.ROUTE.CONVERSATION_HISTORY.LINK,
      activeNameList: [
        $constants.ROUTE.CONVERSATION_HISTORY.NAME,
      ],
    },
    {
      id: $constants.ENUM.SOCIAL_MENU.ACCESS_MANAGEMENT,
      icon: '/images/icons/ic-access-management.svg',
      iconActive: '/images/icons/ic-access-management.svg',
      name: t('sidebar.accessManagement'),
      // path: $constants.ROUTE.USER.LINK,
      isShowSubMenu: true,
      activeNameList: [
        $constants.ROUTE.APPLICATION.NAME,
        $constants.ROUTE.APPLICATION_MANAGE.NAME,
        $constants.ROUTE.SCOPE.NAME,
        $constants.ROUTE.SCOPE_MANAGE.NAME,
        $constants.ROUTE.USER.NAME,
        $constants.ROUTE.USER_MANAGE.NAME,
      ],
      subMenu: [
        // {
        //   name: 'Application',
        //   path: $constants.ROUTE.APPLICATION.LINK,
        //   activeNameList: [
        //     $constants.ROUTE.APPLICATION.NAME,
        //     $constants.ROUTE.APPLICATION_MANAGE.NAME,
        //   ],
        // },
        {
          id: $constants.ENUM.SOCIAL_MENU.MANAGE_ROLE,
          name: t('sidebar.manageRole'),
          path: $constants.ROUTE.ROLES.LINK,
          activeNameList: [
            $constants.ROUTE.ROLES.NAME,
            $constants.ROUTE.ROLES_MANAGE.NAME,
          ],
        },
        {
          id: $constants.ENUM.SOCIAL_MENU.MANAGE_USER,
          name: t('sidebar.manageUser'),
          path: $constants.ROUTE.USER.LINK,
          activeNameList: [
            $constants.ROUTE.USER.NAME,
            $constants.ROUTE.USER_MANAGE.NAME,
          ],
        },
      ],
    },
    {
      id: $constants.ENUM.SOCIAL_MENU.HISTORY_LOG,
      icon: '/images/icons/ic-history-log.svg',
      iconActive: '/images/icons/ic-history-log.svg',
      name:  t('sidebar.historyLog'),
      path: $constants.ROUTE.HISTORY_LOG.LINK,
      activeNameList: [
        $constants.ROUTE.HISTORY_LOG.NAME,
      ],
    },
    {
      id: $constants.ENUM.SOCIAL_MENU.AI_ACCURACY_REPORT,
      icon: '/images/icons/ic-ai-accuracy-report.svg',
      iconActive: '/images/icons/ic-ai-accuracy-report.svg',
      name: t('sidebar.report'),
      path: $constants.ROUTE.AI_ACCURACY_REPORT.LINK,
      activeNameList: [
        $constants.ROUTE.AI_ACCURACY_REPORT.NAME,
      ],
    },
  ],
});

onMounted(() => {
  getUserData();
});

const onClickMenu = (index) => {
  layoutData.dataList = layoutData.dataList.map((e, i) => {
    e.isActive = i === index;
    return e;
  });
};

const checkPath = () => {
  layoutData.dataList = layoutData.dataList.map((e) => {
    if (e.subMenu) {
      e.subMenu.forEach((sub) => {
        if (sub.activeNameList.indexOf($router.currentRoute.value.name) >= 0) {
          e.isShowSubMenu = true;
        }
      });
    }
    // else {
    e.isActive = e.activeNameList.indexOf($router.currentRoute.value.name) >= 0;
    // }
    return e;
  });
};

const getUserData = async () => {
  const accessToken = $functions.getLocalStorage(
    $constants.LOCAL_STORAGE.OAUTH_BACK_OFFICE_ACCESS_TOKEN
  );
  const data = $functions.decodeToken(accessToken);
  layoutData.userName = data.username;
  gUserAuthData.value = data;
  await filterMenuByPermissions();
};

const filterMenuByPermissions = () => {
  const permissions = gUserAuthData.value?.roles?.[0]?.permissions || [];
  const permissionIds = permissions.map(p => p.permission);
  
  layoutData.dataList = layoutData.dataList.filter(menu => {
    if (menu.subMenu && menu.subMenu.length > 0) {
      menu.subMenu = menu.subMenu.filter(sub => 
        permissionIds.includes(sub.id?.toString())
      );
      return menu.subMenu.length > 0;
    } else {
      return permissionIds.includes(menu.id?.toString());
    }
  });
};

setTimeout(() => {
  checkPath();
}, 1);
</script>

<style lang="scss">
.is-main-layout {
  display: flex;
}
</style>
